"use client";

import { motion } from "framer-motion";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { BodyText, HeadlineBodyText } from "src/components/KinsaTypography";

// List of conditions from Figma
const CONDITIONS = [
  "COVID-19",
  "Strep throat",
  "Sinus infections",
  "Flu",
  "Pneumonia",
  "Croup",
  "Cold",
  "RSV",
  "Stomach virus",
  "Hand foot mouth",
  "Pink eye",
  "Allergies",
  "Impetigo",
  "Whooping cough",
  "Rashes",
  "Hives",
  "Sunburns",
  "Cuts & scrapes",
  "Diarrhea",
  "Vomiting",
  "Fevers",
  "Nausea",
  "Headaches",
  "Runny nose",
  "Constipation",
  "UTIs",
  "Vaginal yeast infections",
  "Cold sores",
  "Stomach & GI issues",
  "Sleep issues",
  "Acne",
  "Acid reflux",
  "Epi-Pens",
  "Dandruff",
  "Eczema",
  "Joint & back pain",
  "Athlete's foot",
  "Rosacea",
  "Male hair loss",
  "Eyelash growth",
  "Tooth pain",
  "Birth control",
  "Bacterial vaginosis",
  "Gout",
  "Anti-aging skin care",
  "Genital warts (HPV)",
  "Hypothyroidism",
];

const ROW_COUNT = 7;
const SPEED = 25; // pixels per second

// Split conditions into rows
function getRowConditions(
  conditions: string[],
  rowIndex: number,
  rowCount: number
): string[] {
  return conditions.filter((_, i) => i % rowCount === rowIndex);
}

const ConditionPill = ({ condition }: { condition: string }) => (
  <div className="inline-flex items-center justify-center px-[12px] py-[12px] bg-[rgba(241,249,252,0.05)] border border-[0.5px] border-[rgba(241,249,252,0.05)] rounded-[4px] whitespace-nowrap mr-[4px]">
    <span className="text-white text-[14px] font-normal">{condition}</span>
  </div>
);

interface MarqueeRowProps {
  rowConditions: string[];
}

const MarqueeRow = ({ rowConditions }: MarqueeRowProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [blockWidth, setBlockWidth] = useState(0);

  useLayoutEffect(() => {
    if (containerRef.current && blockRef.current) {
      const measure = () => {
        if (containerRef.current && blockRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
          setBlockWidth(blockRef.current.offsetWidth);
        }
      };

      measure();
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
  }, [rowConditions]);

  // Fallbacks for when measurements are zero
  const defaultContainerWidth = 600;
  const defaultBlockWidth = rowConditions.length * 120;

  const effectiveContainerWidth = containerWidth || defaultContainerWidth;
  const effectiveBlockWidth = blockWidth || defaultBlockWidth;

  const duration = effectiveBlockWidth ? effectiveBlockWidth / SPEED : 40;

  const copies =
    effectiveContainerWidth && effectiveBlockWidth
      ? Math.ceil(effectiveContainerWidth / effectiveBlockWidth) + 1
      : 2;

  const blocks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < copies; i++) {
      arr.push(
        <div key={i} className="flex">
          {rowConditions.map((condition, idx) => (
            <ConditionPill key={idx} condition={condition} />
          ))}
        </div>
      );
    }
    return arr;
  }, [copies, rowConditions]);

  // Animation always scrolls leftward; reverse logic removed
  const animateX = effectiveBlockWidth ? [0, -effectiveBlockWidth] : [0, 0];

  return (
    <div ref={containerRef} className="w-full overflow-hidden relative">
      <motion.div
        className="flex flex-nowrap whitespace-nowrap"
        animate={effectiveBlockWidth ? { x: animateX } : { x: 0 }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: duration,
            ease: "linear",
          },
        }}
      >
        <div ref={blockRef} className="flex">
          {rowConditions.map((condition, idx) => (
            <ConditionPill key={idx} condition={condition} />
          ))}
        </div>
        {blocks.slice(1)}
      </motion.div>
    </div>
  );
};

/**
 * Animated conditions carousel section
 * Shows scrolling rows of condition pills using framer-motion
 */
export function ConditionsCarousel() {
  return (
    <div className="w-full">
      <div className="mb-[32px] text-center">
        <HeadlineBodyText className="text-white">
          Care for 30+ Conditions
        </HeadlineBodyText>
        <BodyText className="text-slate-300">
          Get prescriptions, care plans, and follow-ups for conditions like
          these:
        </BodyText>
      </div>

      {/* Scrolling container - overflow hidden to clip animation */}
      <div className="relative overflow-hidden w-[calc(100%+48px)] -ml-[24px]">
        <div className="flex flex-col gap-[4px]">
          {Array.from({ length: ROW_COUNT }).map((_, rowIndex) => {
            const rowConditions = getRowConditions(
              CONDITIONS,
              rowIndex,
              ROW_COUNT
            );
            return <MarqueeRow key={rowIndex} rowConditions={rowConditions} />;
          })}
        </div>
      </div>
    </div>
  );
}
